import { API_BASE } from '../config';

// Normaliza a URL de uma imagem salva. Os arquivos SEMPRE ficam em
// <API_BASE>/uploads/..., então reconstruímos a URL a partir do trecho
// "/uploads/...". Assim a imagem carrega mesmo que o valor salvo no banco
// tenha uma base errada (problema do app_base_url / pasta aninhada no servidor),
// e também funciona para uploads antigos já gravados com a URL torta.
export function urlImagem(u?: string | null): string {
  if (!u) return '';
  const i = u.indexOf('/uploads/');
  if (i >= 0) return API_BASE + u.slice(i);
  if (/^https?:\/\//i.test(u)) return u;          // URL externa qualquer
  return API_BASE + '/' + u.replace(/^\/+/, '');  // caminho relativo solto
}

// Reduz/recomprime a imagem NO NAVEGADOR antes de enviar, para não estourar os
// limites de upload do PHP (upload_max_filesize / post_max_size). Converte para
// WebP (leve e mantém transparência). Tipos não-imagem ou GIF passam intactos, e
// qualquer falha cai no original — nunca quebra o envio.
export async function comprimirImagem(file: File, maxLado = 1600, qualidade = 0.82): Promise<File> {
  if (!file.type.startsWith('image/') || file.type === 'image/gif') return file;
  try {
    const dataUrl: string = await new Promise((res, rej) => {
      const fr = new FileReader();
      fr.onload = () => res(String(fr.result));
      fr.onerror = () => rej(new Error('read'));
      fr.readAsDataURL(file);
    });
    const img: HTMLImageElement = await new Promise((res, rej) => {
      const im = new Image();
      im.onload = () => res(im);
      im.onerror = () => rej(new Error('img'));
      im.src = dataUrl;
    });
    let w = img.naturalWidth || img.width;
    let h = img.naturalHeight || img.height;
    if (!w || !h) return file;
    if (w > maxLado || h > maxLado) {
      if (w >= h) { h = Math.round((h * maxLado) / w); w = maxLado; }
      else { w = Math.round((w * maxLado) / h); h = maxLado; }
    }
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, w, h);
    const blob: Blob | null = await new Promise((res) => canvas.toBlob(res, 'image/webp', qualidade));
    if (!blob || blob.size >= file.size) return file; // não ajudou → mantém o original
    return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.webp', { type: 'image/webp' });
  } catch {
    return file;
  }
}

// Upload nativo do video do celular (sem recompressao no navegador).
export const VIDEO_OS_MAX_SEGUNDOS = 15;
export const VIDEO_OS_MAX_BYTES = 8 * 1024 * 1024;

export function arquivoEhVideo(file: File): boolean {
  return file.type.startsWith('video/') || /\.(webm|mp4|mov|m4v)$/i.test(file.name);
}

export function urlEhVideo(url?: string | null): boolean {
  return /\.(webm|mp4|mov|m4v)(\?|#|$)/i.test(url || '');
}

function esperarEvento<T extends Event>(alvo: EventTarget, evento: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const ok = (e: Event) => {
      limpar();
      resolve(e as T);
    };
    const erro = () => {
      limpar();
      reject(new Error('Não foi possível ler o vídeo.'));
    };
    const limpar = () => {
      alvo.removeEventListener(evento, ok);
      alvo.removeEventListener('error', erro);
    };
    alvo.addEventListener(evento, ok, { once: true });
    alvo.addEventListener('error', erro, { once: true });
  });
}

// Celulares (iOS .mov e muitos Android .mp4) reportam video.duration = Infinity
// logo apos loadedmetadata. Forcar um seek para um tempo enorme faz o navegador
// recalcular a duracao real e disparar 'durationchange'. Sem isso, a duracao
// vinha 0 e a validacao rejeitava todo video gravado no celular.
function forcarDuracaoReal(video: HTMLVideoElement): Promise<number> {
  return new Promise((resolve) => {
    let resolvido = false;
    const concluir = (valor: number) => {
      if (resolvido) return;
      resolvido = true;
      video.removeEventListener('durationchange', aoMudar);
      window.clearTimeout(timer);
      try {
        video.currentTime = 0;
      } catch {
        /* ignora: alguns navegadores não permitem reposicionar aqui */
      }
      resolve(valor);
    };
    const aoMudar = () => {
      if (Number.isFinite(video.duration) && video.duration > 0) {
        concluir(video.duration);
      }
    };
    video.addEventListener('durationchange', aoMudar);
    // Timeout de seguranca: nao trava se o navegador nunca recalcular.
    const timer = window.setTimeout(() => concluir(0), 3000);
    try {
      video.currentTime = 1e101;
    } catch {
      concluir(0);
    }
  });
}

async function duracaoVideo(file: File): Promise<number> {
  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  video.preload = 'metadata';
  video.muted = true;
  video.playsInline = true;
  video.src = url;
  try {
    await esperarEvento(video, 'loadedmetadata');
    let duracao = video.duration;
    if (!Number.isFinite(duracao) || duracao <= 0) {
      duracao = await forcarDuracaoReal(video);
    }
    return Number.isFinite(duracao) ? duracao : 0;
  } finally {
    URL.revokeObjectURL(url);
    video.removeAttribute('src');
    video.load();
  }
}

export async function validarVideoOS(file: File): Promise<number> {
  if (!arquivoEhVideo(file)) throw new Error('Selecione um arquivo de vídeo.');
  const duracao = await duracaoVideo(file);
  if (!duracao || duracao > VIDEO_OS_MAX_SEGUNDOS + 0.25) {
    throw new Error(`O vídeo deve ter no máximo ${VIDEO_OS_MAX_SEGUNDOS} segundos.`);
  }
  return duracao;
}

// Upload NATIVO: o video gravado pelo celular ja vem em H.264 720p/1080p
// eficiente. NAO recomprimimos no navegador — o re-encode em tempo real (canvas
// + MediaRecorder) cortava o video no mobile e era lento. Aqui so validamos
// duracao e tamanho e mandamos o arquivo original.
export async function prepararVideoOS(file: File): Promise<File> {
  if (!arquivoEhVideo(file)) throw new Error('Selecione um arquivo de vídeo.');
  // Duracao ilegivel (0) nao bloqueia: webm gravado pelo MediaRecorder muitas
  // vezes nao traz duracao no cabeçalho. So barramos se der pra ler E passar.
  const dur = await duracaoVideo(file).catch(() => 0);
  if (dur && dur > VIDEO_OS_MAX_SEGUNDOS + 0.5) {
    throw new Error(`O vídeo deve ter no máximo ${VIDEO_OS_MAX_SEGUNDOS} segundos.`);
  }
  if (file.size > VIDEO_OS_MAX_BYTES) {
    const mb = Math.round(VIDEO_OS_MAX_BYTES / (1024 * 1024));
    throw new Error(
      `O vídeo ficou acima de ${mb} MB. Grave um clipe mais curto ou reduza a resolução da câmera (ex.: 720p) e tente novamente.`,
    );
  }
  return file;
}

export interface Usuario {
  id: number;
  nome: string;
  email: string;
  telefone?: string | null;
  whatsapp?: string | null;
  pode_editar_inicio_os?: boolean;
  pode_ver_localizacao_tecnico?: boolean;
  pode_trazer_tecnico_associado_unidade?: boolean;
  pode_retirar_tipo_chamado_para_equipamento?: boolean;
  pode_ver_equipamentos_nas_os?: boolean;
  ocultar_custo_mao_obra_relatorio_chamados?: boolean;
  ocultar_valor_hora_tecnico_relatorio_chamados?: boolean;
  pode_ver_materiais_os?: boolean;
  pode_adicionar_obs_conclusao_os?: boolean;
  pode_editar_obs_conclusao_os?: boolean;
  // Permissao de usuario "TECNICO NAO PODE CRIAR OS": ligada, o tecnico deixa
  // de conseguir abrir OS (tela e API - ver os/os_criar.php).
  tecnico_nao_pode_criar_os?: boolean;
  pode_transferir_os_whatsapp?: boolean;
  pode_encaminhar_assinatura_whatsapp?: boolean;
  pode_cadastrar_material?: boolean;
  perfil: string;
  empresa_id: number | null;
  empresa_nome?: string | null;
  plano?: string | null;
  db_nome?: string | null;
  modo_visualizacao?: 'facilities' | 'oficina';
  registro_consumo_habilitado?: boolean;
  alterar_ordem_assinatura_habilitado?: boolean;
  rotulo_numero_serie?: string;
  // Rotulo do campo ativos.nome (vazio = "Nome" no cadastro, "Equipamento" no resto).
  rotulo_nome_ativo?: string;
  quantidade_usuarios_permitidos?: number | null;
  orcamento_os_habilitado?: boolean;
  filtro_patrimonio_habilitado?: boolean;
  rastreamento_tecnico_habilitado?: boolean;
  modo_financeiro_habilitado?: boolean;
  integracao_pagamentos_habilitado?: boolean;
  qrcode_detalhes_habilitado?: boolean;
  somente_gestor_supervisor_pode_reagendar_habilitado?: boolean;
  lancar_dados_os_concluida_habilitado?: boolean;
  somente_gestor_supervisor_pode_ver_valores_habilitado?: boolean;
  tecnico_sem_acesso_rastreamento_habilitado?: boolean;
  incluir_etapas_processo_os_habilitado?: boolean;
  servicos_habilitado?: boolean;
  dados_equipamento_os_materiais_habilitado?: boolean;
  criacao_os_simplificada_habilitado?: boolean;
  // Permissão de empresa "RETIRAR UNIDADE - LOCAL - PISO": esconde a hierarquia
  // Unidade > Piso > Local em todo o sistema, sem barrar OS nem equipamento.
  retirar_unidade_local_piso_habilitado?: boolean;
  // Permissão de empresa "RETIRAR TELA PMOC": o módulo PMOC deixa de existir para
  // a empresa — menu, rotas, abas, atalhos e relatório.
  retirar_tela_pmoc_habilitado?: boolean;
  // Permissão de empresa "EXCLUIR UNIDADE/PISO/LOCAL SEM HISTÓRICO": libera o
  // botão Excluir (além de Ativar/Desativar) em Cadastros. Apaga só o registro;
  // pisos, locais, equipamentos e OS vinculados ficam intactos.
  unidade_local_delete_habilitado?: boolean;
  suporte_acesso?: {
    usuario_id: number;
    nome: string;
    email: string;
    perfil: string;
  } | null;
}

// Vínculo (membership): uma das empresas às quais a pessoa pertence.
export interface EmpresaVinculo {
  empresa_id: number;
  empresa_nome: string;
  perfil: string;
  plano?: string | null;
  modo_visualizacao?: 'facilities' | 'oficina';
  registro_consumo_habilitado?: boolean;
  alterar_ordem_assinatura_habilitado?: boolean;
  rotulo_numero_serie?: string;
  // Rotulo do campo ativos.nome (vazio = "Nome" no cadastro, "Equipamento" no resto).
  rotulo_nome_ativo?: string;
  quantidade_usuarios_permitidos?: number | null;
  orcamento_os_habilitado?: boolean;
  filtro_patrimonio_habilitado?: boolean;
  rastreamento_tecnico_habilitado?: boolean;
  pode_ver_equipamentos_nas_os?: boolean;
  ocultar_custo_mao_obra_relatorio_chamados?: boolean;
  ocultar_valor_hora_tecnico_relatorio_chamados?: boolean;
  modo_financeiro_habilitado?: boolean;
  integracao_pagamentos_habilitado?: boolean;
  qrcode_detalhes_habilitado?: boolean;
  somente_gestor_supervisor_pode_reagendar_habilitado?: boolean;
  lancar_dados_os_concluida_habilitado?: boolean;
  somente_gestor_supervisor_pode_ver_valores_habilitado?: boolean;
  tecnico_sem_acesso_rastreamento_habilitado?: boolean;
  incluir_etapas_processo_os_habilitado?: boolean;
  servicos_habilitado?: boolean;
  dados_equipamento_os_materiais_habilitado?: boolean;
}

const TOKEN_KEY = 'lizzi_token';
const USER_KEY = 'lizzi_user';
const EMPRESAS_KEY = 'lizzi_empresas';

export const getToken = (): string => localStorage.getItem(TOKEN_KEY) || '';
export const getUser = (): Usuario | null => {
  try { return JSON.parse(localStorage.getItem(USER_KEY) || 'null'); } catch { return null; }
};
export const getEmpresas = (): EmpresaVinculo[] => {
  try { return JSON.parse(localStorage.getItem(EMPRESAS_KEY) || '[]'); } catch { return []; }
};
export const setEmpresas = (lista: EmpresaVinculo[]): void => {
  localStorage.setItem(EMPRESAS_KEY, JSON.stringify(lista || []));
};
export const setSession = (token: string, usuario: Usuario | null): void => {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(usuario || null));
};
export const clearSession = (): void => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(EMPRESAS_KEY);
};

interface Opts {
  method?: string;
  body?: unknown;
  isForm?: boolean;
  includeAuth?: boolean;
  redirectOn401?: boolean;
}

// Recurso exclusivo do plano Premium (backend responde 402). No modo Simples
// esses recursos ficam ocultos (menu, rotas e chamadas já são filtrados por
// isPremium). Se ainda assim uma chamada vazar até aqui, interrompemos o fluxo
// SEM mensagem — a `message` vazia faz os blocos `{erro && ...}` não renderarem.
export class RecursoPremiumError extends Error {
  constructor() {
    super('');
    this.name = 'RecursoPremiumError';
  }
}

// Chamada à API. Retorna o `data` da resposta padrão { success, data, message }.
export async function api<T = any>(
  path: string,
  { method = 'GET', body, isForm = false, includeAuth = true, redirectOn401 = true }: Opts = {},
): Promise<T> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (includeAuth && token) headers['Authorization'] = 'Bearer ' + token;

  let payload: BodyInit | undefined;
  if (isForm) {
    payload = body as BodyInit;
  } else if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    payload = JSON.stringify(body);
  }

  // Erro de rede (servidor fora do ar, sem internet, CORS, URL errada): fetch lança TypeError.
  let res: Response;
  try {
    res = await fetch(API_BASE + path, { method, headers, body: payload });
  } catch {
    throw new Error('Não foi possível conectar ao servidor. Verifique sua conexão com a internet e tente novamente em instantes.');
  }

  let json: any = null;
  try { json = await res.json(); } catch { /* resposta não-JSON */ }

  if (res.status === 401) {
    clearSession();
    if (redirectOn401 && !window.location.hash.startsWith('#/login')) window.location.assign('#/login');
    throw new Error(json?.message || 'Sua sessão expirou. Entre novamente para continuar.');
  }

  // 402 = funcionalidade exclusiva do Premium. No modo Simples nunca mostramos
  // a mensagem "Funcionalidade disponível apenas no plano Premium": apenas
  // abortamos a chamada silenciosamente.
  if (res.status === 402) {
    throw new RecursoPremiumError();
  }

  if (!res.ok) {
    // Mensagem do backend (já em pt-br) tem prioridade; senão, texto amigável por status.
    if (json?.message) throw new Error(json.message);
    const padrao: Record<number, string> = {
      400: 'Não entendemos a solicitação. Confira os dados e tente de novo.',
      403: 'Você não tem permissão para fazer isso.',
      404: 'Não encontramos o que você procura.',
      409: 'Esse registro já existe. Tente com outros dados.',
      422: 'Alguns campos precisam de atenção. Revise e tente novamente.',
      500: 'Tivemos um problema no servidor. Tente novamente em instantes.',
      502: 'O serviço está indisponível no momento. Tente novamente em instantes.',
    };
    throw new Error(padrao[res.status] || 'Algo deu errado. Tente novamente em instantes.');
  }

  return (json && json.data !== undefined ? json.data : json) as T;
}

// Upload de arquivo com progresso real (0..1) via XMLHttpRequest — o fetch()
// nao expoe progresso de upload. Mesmo tratamento de 401/402/erro do api().
export function apiUpload<T = any>(
  path: string,
  form: FormData,
  onProgress?: (fracao: number) => void,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', API_BASE + path);
    const token = getToken();
    if (token) xhr.setRequestHeader('Authorization', 'Bearer ' + token);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
    };
    xhr.onerror = () => reject(new Error('Não foi possível conectar ao servidor. Verifique sua conexão com a internet e tente novamente em instantes.'));
    xhr.onload = () => {
      let json: any = null;
      try { json = JSON.parse(xhr.responseText); } catch { /* resposta não-JSON */ }

      if (xhr.status === 401) {
        clearSession();
        if (!window.location.hash.startsWith('#/login')) window.location.assign('#/login');
        reject(new Error(json?.message || 'Sua sessão expirou. Entre novamente para continuar.'));
        return;
      }
      if (xhr.status === 402) { reject(new RecursoPremiumError()); return; }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve((json && json.data !== undefined ? json.data : json) as T);
        return;
      }
      reject(new Error(json?.message || 'Algo deu errado. Tente novamente em instantes.'));
    };

    xhr.send(form);
  });
}
